import { __decorate, __metadata, __rest } from 'tslib';
import React, { PureComponent } from 'react';
import { Inject } from '@tencent/ioc';
import styles from './toolbar-button.less.js';
import { evaluate } from '../../../controllers/base/evaluate.js';
import { OutlineType } from '../../../services/toolbar/toolbar-config.interface.js';
import { EnvContext } from '../../../controllers/workbench/context.js';
import { ActualButton } from './actual-button.js';
import { ToolbarDropdownButton } from './dropdown-button.js';
import { BuiltinUiState } from '../../../services/store/builtin-state.js';
import { ButtonWrapper } from './button-wrapper.js';
import { IBadgeService } from '../../../services/badge/badge-service.interface.js';
import { ErrorBoundary } from '../../../utils/error-boundary.js';
import { setActionPointEvent } from '../../../providers/action-point/action-point-runner.js';

class ToolbarButton extends PureComponent {
    constructor(props) {
        super(props);
        this.dropdownButton = React.createRef();
        this.badgeConsumed = false;
        this.getDropdownProps = () => {
            var _a, _b, _c, _d;
            const props = Object.assign(Object.assign({}, this.props), { disabled: !this.isEnable(), unionClick: this.props.onArrowClick === undefined
                    && !((_a = this.props.onClick) === null || _a === void 0 ? void 0 : _a.hasRaw), isFocusArrow: (_b = this.props.isFocusArrow) !== null && _b !== void 0 ? _b : false, alignment: (_c = this.props.dropdownAlignment) !== null && _c !== void 0 ? _c : 'left', dropdownPlacement: (_d = this.props.dropdownPlacement) !== null && _d !== void 0 ? _d : 'auto', hasArrow: this.isArrow(), onButtonClick: this.handleClick, onButtonDoubleClick: this.handleDoubleClick, onDropdownClick: this.handleDropdownClick, onVisibleChange: this.onVisibleChange, ariaAttributes: this.getAriaAttributes(), renderChildren: this.getChild.bind(this) });
            return props;
        };
        this.onVisibleChange = (visible) => {
            var _a, _b;
            (_b = (_a = this.props).onDropdownVisibleChange) === null || _b === void 0 ? void 0 : _b.call(_a, visible);
            this.setState({
                dropdownClosed: !visible,
            }, () => {
                this.setState({ showTooltip: !this.props.isFocus && this.isShowToolTip(this.state.showTooltip) });
            });
            if (visible) {
                this.handleBadge();
            }
        };
        this.handleClick = (event) => {
            this.context.store.update(BuiltinUiState.ToolbarEvent, { target: this.props.name, type: 'click' });
            this.context.store.update(BuiltinUiState.isFocusedByClick, true);
            if (this.isEnable() && this.props.onClick) {
                setActionPointEvent(this.props.onClick, event);
                this.props.onClick();
            }
            if (!this.isEnable()) {
                event.stopPropagation();
            }
            else {
                this.handleBadge();
            }
        };
        this.handleDoubleClick = (event) => {
            this.context.store.update(BuiltinUiState.ToolbarEvent, { target: this.props.name, type: 'doubleClick' });
            if (this.isEnable() && this.props.onDoubleClick) {
                this.props.onDoubleClick();
            }
            if (!this.isEnable()) {
                event.stopPropagation();
            }
            else {
                this.handleBadge();
            }
        };
        this.handleDropdownClick = (event) => {
            this.context.store.update(BuiltinUiState.ToolbarEvent, { target: this.props.name, type: 'dropdownOpen' });
            if (this.isEnable() && this.props.onArrowClick) {
                this.props.onArrowClick();
            }
            event.stopPropagation();
        };
        this.getChild = (dropdownProps, otherProps, ref) => {
            const { icon, style, color, name, text = '', content, widthRange, maxWidth: forceMaxWidth, onClick, onDoubleClick, } = this.props;
            const { style: otherStyles } = otherProps, restOtherProps = __rest(otherProps, ["style"]);
            const maxWidth = forceMaxWidth !== null && forceMaxWidth !== void 0 ? forceMaxWidth : widthRange === null || widthRange === void 0 ? void 0 : widthRange[1];
            const styleProp = Object.assign(Object.assign(Object.assign({}, style), otherStyles), { maxWidth });
            if (dropdownProps.dropdownContent) {
                styleProp.paddingRight = 0;
            }
            if (content) {
                return (React.createElement("div", { id: `toolbar-button-${name}`, style: styleProp, onClick: onClick, onDoubleClick: onDoubleClick }, content));
            }
            const buttonProp = {
                name,
                clickable: !dropdownProps.unionClick,
                style: styleProp,
                color,
            };
            const ariaAttrib = this.getAriaAttributes();
            return React.createElement(ActualButton, Object.assign({}, buttonProp, { icon: icon, text: text, ariaAttributes: ariaAttrib }, restOtherProps, { ref: ref }));
        };
        this.handleDropdownVisibleChange = (visible) => {
            var _a;
            if (this.dropdownButton) {
                (_a = this.dropdownButton.current) === null || _a === void 0 ? void 0 : _a.handleVisibleChange(visible);
            }
        };
        this.state = {
            dropdownClosed: true,
            showTooltip: false,
        };
    }
    getWrapperClass() {
        const { className, isFocus, text, verticallyArrange, outlineType = OutlineType.DEFAULT, preventHighlight, } = this.props;
        let wrapperClass = styles.wrapper;
        if (className) {
            wrapperClass += ` ${className}`;
        }
        if (this.isChecked()) {
            wrapperClass += ` ${styles.checked}`;
        }
        if (!this.isEnable()) {
            wrapperClass += ` ${styles.disabled}`;
            // 自定义类型不可用的时候加上, 用以阻止事件向下传
            if (this.props.content) {
                wrapperClass += ` ${styles['prevent-capture']}`;
            }
        }
        if (isFocus) {
            wrapperClass += ` ${styles['toolbar-item-focus']}`;
        }
        if (text && !verticallyArrange) {
            wrapperClass += ` ${styles[outlineType]}`;
        }
        if (preventHighlight) {
            wrapperClass += ` ${styles['no-hover']}`;
        }
        return wrapperClass;
    }
    getAriaAttributes() {
        const result = {};
        const { ariaLabel, tooltip, text, } = this.props;
        result['aria-disabled'] = !this.isEnable();
        const defaultTip = typeof tooltip === 'string' ? tooltip : undefined;
        let defaultLabel = ariaLabel;
        if (defaultLabel === undefined || defaultLabel === '') {
            defaultLabel = text;
        }
        if (defaultLabel === undefined || defaultLabel === '') {
            defaultLabel = defaultTip;
        }
        result['aria-label'] = defaultLabel;
        const hasPopUp = this.isArrow();
        if (hasPopUp) {
            result['aria-haspopup'] = true;
            result['aria-expanded'] = !this.state.dropdownClosed;
        }
        if (this.props.checked !== undefined) {
            result['aria-pressed'] = this.isChecked();
        }
        return result;
    }
    handleBadge() {
        if (this.props.badge && !this.props.disabled) {
            this.badgeService.removeBadgeOn('toolbar', this.props.name);
        }
    }
    closeDropdown() {
        var _a;
        (_a = this.dropdownButton.current) === null || _a === void 0 ? void 0 : _a.handleHiddenDropdown();
    }
    /** 判断当前按钮是否显示下拉箭头 */
    isArrow() {
        var _a;
        const { hasArrow, onArrowClick, dropdownContent } = this.props;
        return !!((_a = (hasArrow && dropdownContent)) !== null && _a !== void 0 ? _a : onArrowClick);
    }
    render() {
        const classNames = this.getWrapperClass();
        this.badgeConsumed = false;
        const dropdownProps = this.getDropdownProps();
        const wrapperProps = {
            className: classNames,
            showToolTip: this.isShowToolTip(this.state.showTooltip),
            badge: this.badgeConsumed ? false : (this.props.badge && !this.props.disabled),
            placement: this.props.placement,
            item: this.props,
            checkColor: this.props.checked ? evaluate(this.props.highlightColor, '') : '',
            hasArrow: this.isArrow(),
            onVisibleChange: (showToolTip) => {
                this.setState({ showTooltip: this.isShowToolTip(showToolTip) });
            },
        };
        return (React.createElement(ErrorBoundary, null,
            React.createElement(ButtonWrapper, Object.assign({}, wrapperProps, { key: this.props.name }),
                React.createElement(ToolbarDropdownButton, Object.assign({ ref: this.dropdownButton }, dropdownProps)))));
    }
    isShowToolTip(showTooltip) {
        if (this.state.dropdownClosed === false) {
            return false;
        }
        if (this.props.isFocus) {
            return true;
        }
        return showTooltip;
    }
    isChecked() {
        var _a;
        // feature 要求高亮或下拉菜单打开时
        return (_a = this.props.checked) !== null && _a !== void 0 ? _a : (this.state.dropdownClosed === false);
    }
    isEnable() {
        return !this.props.disabled;
    }
}
ToolbarButton.contextType = EnvContext;
ToolbarButton.defaultProps = {
    // config
    hasArrow: true,
    text: '',
    tooltip: '',
    // props
    disabled: false,
    checked: false,
    badge: false,
};
__decorate([
    Inject(IBadgeService),
    __metadata("design:type", Object)
], ToolbarButton.prototype, "badgeService", void 0);

export { ToolbarButton };
//# sourceMappingURL=toolbar-button.js.map
